import React, { useEffect, useRef, useState } from "react";
import InsightsDashboard, {
  IDChartData,
  IDDateRangeOption,
  InsightsDashboardTableData,
} from "src/app/components/InsightsDashboard/InsightsDashboard";
import InsightsDashboardSetupPane, {
  SETUP_MODE_QUERY_PARAM_KEY,
  SetupModes,
} from "src/app/components/InsightsDashboard/panes/InsightsDashboardSetupPane/InsightsDashboardSetupPane";
import { showErrorToast } from "src/utils/methods";
import axios from "axios";
import { API_URLS } from "src/utils/API_URLS";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/reducers";
import { InsightsDashboardFilters } from "src/redux/insights-dashboard/types";
import { setSelectedDashboard } from "src/redux";
import { useQuery } from "src/app/methods/useQuery";
import {
  getFiltersFromSetup,
  mapInsightsDashboardDateRangeToRequest,
  mapInsightsDashboardSetupToRequest,
} from "src/app/components/InsightsDashboard/utils";

export const SocialInsightsDashboardDetailPage = () => {
  const [data, setData] = useState<InsightsDashboardTableData | null>(null);
  const [comparisonData, setComparisonData] =
    useState<InsightsDashboardTableData | null>(null);
  const [chartData, setChartData] = useState<IDChartData | null>(null);

  const [loading, setLoading] = useState(false);

  const query = useQuery();
  const setupMode = query.get(SETUP_MODE_QUERY_PARAM_KEY) as
    | SetupModes
    | undefined;

  const dispatch = useDispatch();

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const {
    setup,
    setupConfig,
    dateRanges,
    selectedDashboard,
    dateFrom,
    dateTo,
    dateRange,
  } = useSelector((state: RootState) => state.insightsDashboardReducer);

  const blockDebounce = useRef(true);
  // Set by InsightsDashboard's dashboard-switch effect right before it dispatches
  // setSetup and fetches the new dashboard's data itself, so the [setup] effect
  // below doesn't fire a redundant, duplicate fetch for the same transition.
  const skipSetupFetch = useRef(false);

  const getData = async (
    filters: InsightsDashboardFilters,
    overrides?: {
      dateRange?: string | null;
      dateFrom?: string | null;
      dateTo?: string | null;
      hashtagsCriteria?: string;
      mentionsCriteria?: string;
      hashtagsMentionsCriteria?: string;
      workspacePublicationsType?: string;
    },
  ) => {
    try {
      setLoading(true);

      const payload = {
        dateFrom: overrides?.dateFrom ?? dateFrom,
        dateTo: overrides?.dateTo ?? dateTo,
        dateRange: overrides?.dateRange ?? dateRange?.value,
        workspacePublicationsType:
          overrides?.workspacePublicationsType ??
          setup.workspacePublicationsType,
        hashtagsCriteria:
          overrides?.hashtagsCriteria ?? setup.hashtagsCriteria?.value ?? "or",
        mentionsCriteria:
          overrides?.mentionsCriteria ?? setup.mentionsCriteria?.value ?? "or",
        hashtagsMentionsCriteria:
          overrides?.hashtagsMentionsCriteria ??
          setup.hashtagsMentionsCriteria?.value ??
          "or",
        wsWorkspaceUuid: activeWorkspaceUuid,
        ...filters,
        countries: filters.countries.map((country) => country.value),
      };

      const { data: content } = await axios.post(
        API_URLS.getSocialInsightsStats,
        payload,
      );

      setData(content.content);
      getChartData(payload);
      getComparisonData(payload);
    } catch (e) {
      showErrorToast();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!setupConfig) return;
    // The dashboard-switch effect in InsightsDashboard already triggers its own
    // explicit getData with the correct hydrated filters, and also dispatches
    // setSetup. Without this guard, that dispatch would fire a second, duplicate
    // fetch here for the same transition.
    if (skipSetupFetch.current) {
      skipSetupFetch.current = false;
      return;
    }
    getData(getFiltersFromSetup(setup, setupConfig));
  }, [setup]);

  const getComparisonData = async (payload: any) => {
    setComparisonData(null);

    try {
      const { data: content } = await axios.post(
        API_URLS.getSocialInsightsStatsComparison,
        payload,
      );

      setComparisonData(content.content);
    } catch (e) {
      showErrorToast();
    }
  };

  const getChartData = async (payload: any) => {
    setChartData(null);

    try {
      const { data: content } = await axios.post(
        API_URLS.getSocialInsightsChartData,
        payload,
      );

      setChartData(content.content);
    } catch (e) {
      showErrorToast();
    }
  };

  const updateDashboard = (overrides?: {
    dateRange?: IDDateRangeOption | null;
    dateFrom?: string | null;
    dateTo?: string | null;
  }) => {
    if (!selectedDashboard) return;

    // showTrends is a local-only UI preference and must never be sent to the backend.
    const { showTrends: _showTrends, ...filtersWithoutShowTrends } =
      selectedDashboard.filters;

    const newDashboard = {
      ...selectedDashboard,
      filters: {
        ...filtersWithoutShowTrends,
        dateFrom: overrides?.dateFrom ?? dateFrom,
        dateTo: overrides?.dateTo ?? dateTo,
        dateRange: overrides?.dateRange ?? dateRange,
      },
    };

    const requestDashboard = {
      ...newDashboard,
      filters: {
        ...newDashboard.filters,
        ...mapInsightsDashboardSetupToRequest(setup),
        dateRange: mapInsightsDashboardDateRangeToRequest(
          newDashboard.filters.dateRange,
        ),
      },
    };

    axios.post(API_URLS.editInsightsDashboard, {
      ...requestDashboard,
      wsWorkspaceUuid: activeWorkspaceUuid,
    });
    dispatch(setSelectedDashboard(requestDashboard));
  };

  return (
    <>
      <InsightsDashboard
        getData={getData}
        loading={loading}
        data={data}
        comparisonData={comparisonData}
        chartData={chartData}
        dateFrom={dateFrom}
        dateTo={dateTo}
        dateRanges={dateRanges}
        selectedDateRange={dateRange}
        blockDebounce={blockDebounce}
        skipSetupFetch={skipSetupFetch}
      />

      {setupMode && (
        <InsightsDashboardSetupPane
          loading={loading}
          showPane={Boolean(setupMode)}
          blockDebounce={blockDebounce}
          setupMode={setupMode}
        />
      )}
    </>
  );
};
